import React from 'react';
import { Phone, Mail, MapPin, MessageCircle } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { useTranslation } from 'react-i18next';
import { addLanguagePrefix, useCurrentLanguage, useLanguageNavigate } from '../utils/languageRouting';
import { locationRoutes } from '../data/locationRoutes';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const currentLanguage = useCurrentLanguage();
  const languageNavigate = useLanguageNavigate();

  const serviceLinks = [
    { key: 'mockData.services.terraces.title', hash: 'terraces' },
    { key: 'mockData.services.poolSurrounds.title', hash: 'pool_surrounds' },
    { key: 'mockData.services.driveways.title', hash: 'driveways' },
    { key: 'mockData.services.stairs.title', hash: 'stairs_steps' },
    { key: 'mockData.services.balconies.title', hash: 'balconies' },
    { key: 'mockData.services.walkways.title', hash: 'walkways_patios' },
    { key: 'mockData.services.garages.title', hash: 'garages' }
  ];

  const quickLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/gallery', label: t('nav.gallery') }
  ];

  const handleNavigation = (path, hash = null) => {
    languageNavigate(path);
    
    if (hash) {
      // Scroll to specific section
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          const offset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 300);
    } else {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    }
  };

  const getLocalizedPath = (path) => addLanguagePrefix(path, currentLanguage);

  const handleServiceNavigation = (hash) => {
    console.log('[Footer] Navigating to service:', hash);
    
    // Navigate to services page with language prefix
    languageNavigate('/services');
    
    // Wait for page to load, then scroll to section
    setTimeout(() => {
      const element = document.getElementById(hash);
      console.log('[Footer] Looking for element:', hash, 'Found:', !!element);
      
      if (element) {
        const offset = 100; // Account for fixed header
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      } else {
        console.warn(`[Footer] Element with id "${hash}" not found on Services page`);
      }
    }, 800); // Increased timeout to ensure page is fully loaded
  };

  return (
    <footer className="bg-gradient-to-br from-[#2a3a45] via-[#364854] to-[#2a3a45] text-stone-300">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src="/images/costa-stone-logo-master.svg" 
                alt="Costa Stone Logo" 
                width="260"
                height="34"
                loading="lazy"
                decoding="async"
                className="h-8 sm:h-9 w-auto max-w-full"
              />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              {t('footer.companyName')}
              <span className="block text-sm font-normal text-[#519dc7] mt-1">{t('footer.tagline')}</span>
            </h3>
            <p className="text-stone-400 mb-6 leading-relaxed">
              {t('footer.description')}
            </p>
            <a
              href={`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-all hover:shadow-lg hover:scale-105"
            >
              <MessageCircle size={20} />
              <span className="font-medium">{t('footer.whatsappUs')}</span>
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <a
                    href={getLocalizedPath(link.path)}
                    onClick={(event) => {
                      event.preventDefault();
                      handleNavigation(link.path);
                    }}
                    className="text-stone-400 hover:text-[#519dc7] transition-colors inline-block hover:translate-x-1 duration-200 cursor-pointer"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href={getLocalizedPath('/what-is-stone-carpet')}
                  onClick={(event) => {
                    event.preventDefault();
                    handleNavigation('/what-is-stone-carpet');
                  }}
                  className="text-stone-400 hover:text-[#519dc7] transition-colors inline-block hover:translate-x-1 duration-200 cursor-pointer"
                >
                  {t('footer.whatIsStoneCarpet')}
                </a>
              </li>
              <li>
                <a
                  href={getLocalizedPath('/faq')}
                  onClick={(event) => {
                    event.preventDefault();
                    handleNavigation('/faq');
                  }}
                  className="text-stone-400 hover:text-[#519dc7] transition-colors inline-block hover:translate-x-1 duration-200 cursor-pointer"
                >
                  {t('nav.faq')}
                </a>
              </li>
              <li>
                <a
                  href={`${getLocalizedPath('/contact')}#quick-contact`}
                  onClick={(event) => {
                    event.preventDefault();
                    handleNavigation('/contact', 'quick-contact');
                  }}
                  className="text-stone-400 hover:text-[#519dc7] transition-colors inline-block hover:translate-x-1 duration-200 cursor-pointer"
                >
                  {t('nav.contact')}
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">{t('common.ourServices')}</h4>
            <ul className="space-y-3">
              {serviceLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={`${getLocalizedPath('/services')}#${link.hash}`}
                    onClick={(event) => {
                      event.preventDefault();
                      handleServiceNavigation(link.hash);
                    }}
                    className="text-stone-400 hover:text-[#519dc7] transition-colors inline-block hover:translate-x-1 duration-200 cursor-pointer"
                  >
                    {t(link.key)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">{t('footer.serviceAreas')}</h4>
            <ul className="space-y-3">
              {locationRoutes.map((location) => (
                <li key={location.slug}>
                  <a
                    href={getLocalizedPath(location.path)}
                    onClick={(event) => {
                      event.preventDefault();
                      handleNavigation(location.path);
                    }}
                    className="text-stone-400 hover:text-[#519dc7] transition-colors inline-block hover:translate-x-1 duration-200 cursor-pointer"
                  >
                    {location.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">{t('common.contactUs')}</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="flex items-start space-x-3 text-stone-400 hover:text-[#519dc7] transition-colors group"
                >
                  <Phone size={20} className="mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <span>{contactInfo.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-start space-x-3 text-stone-400 hover:text-[#519dc7] transition-colors group"
                >
                  <Mail size={20} className="mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <span className="break-all">{contactInfo.email}</span>
                </a>
              </li>
              <li>
                <div className="flex items-start space-x-3 text-stone-400">
                  <MapPin size={20} className="mt-0.5 flex-shrink-0" />
                  <span>{contactInfo.serviceArea}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-stone-700/50">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-stone-500 text-sm text-center md:text-left">
              &copy; {currentYear} {t('footer.companyName')}. {t('footer.copyright')}
            </p>
            <a
              href={getLocalizedPath('/privacy-policy')}
              onClick={(event) => {
                event.preventDefault();
                handleNavigation('/privacy-policy');
              }}
              className="text-stone-500 hover:text-[#519dc7] text-sm transition-colors"
            >
              {t('footer.privacyPolicy')}
            </a>
            <p className="text-stone-500 text-sm text-center md:text-right">
              {t('footer.premiumInstallations')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
